import React from 'react';
import { CheckIcon } from 'lucide-react';
import { ArchitecturalForm } from '../components/ui/ArchitecturalForm';
import { ButtonLink } from '../components/ui/ButtonLink';
import { CtaBand } from '../components/ui/CtaBand';
import { PageHero } from '../components/ui/PageHero';
import { services } from '../data/services';
import { usePageMeta } from '../hooks/usePageMeta';
const variants = ['stack', 'frame', 'steps'] as const;
export function Expertise() {
  usePageMeta('Our Expertise', 'Executive search, C-suite recruitment, board and non-executive director search, senior leadership recruitment, confidential search, talent mapping, leadership advisory, and succession planning.');
  return <>
      <PageHero label="Our Expertise" title="Executive search services for every level of leadership" intro="From a single critical appointment to long-term succession readiness, each service is built on the same foundation: a clear understanding of what your organization needs from its leaders." visual={<ArchitecturalForm variant="steps" tone="dark" data-id="element-788" data-mp="1x462qy7uld1x" />} data-id="element-787" data-mp="1qkfgbmgrigx2">
        <ButtonLink to="/employers#inquiry" size="lg" data-id="element-789" data-mp="1s8h0uo8cvk2g">
          Discuss Your Hiring Needs
        </ButtonLink>
      </PageHero>

      <nav aria-label="Services on this page" className="border-b border-plum/10 bg-porcelain" data-id="element-790" data-mp="1o7oqtsrntb54">
        <ul className="mx-auto flex max-w-7xl flex-wrap gap-2 px-6 py-6 lg:px-10" data-id="element-791" data-mp="1j8lnccrbvcz9">
          {services.map(s => <li key={s.slug} data-id="element-792" data-mp="19go90x50w4wb">
              <a href={`#${s.slug}`} className="inline-flex h-9 items-center rounded-full border border-plum/15 px-4 text-sm text-plum/75 transition-colors duration-150 hover:border-copper hover:text-copper-deep" data-id="element-793" data-mp="19gftsylfgnhy">
                {s.title}
              </a>
            </li>)}
        </ul>
      </nav>

      {services.map((service, index) => {
      const Icon = service.icon;
      const alt = index % 2 === 1;
      return <section key={service.slug} id={service.slug} aria-labelledby={`${service.slug}-title`} className={alt ? 'bg-limestone' : 'bg-porcelain'} data-id="element-794" data-mp="gtogpmpwi324" data-mpi={index}>
            <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:py-28" data-id="element-795" data-mp="lulxpvz6cr21">
              <div className="lg:col-span-7" data-id="element-796" data-mp="6yei78ypodt3">
                <p className="flex items-center gap-3 font-display text-sm font-semibold text-copper-deep" data-id="element-797" data-mp="bw66lt05dhsj">
                  <Icon className="h-4 w-4" aria-hidden="true" data-id="element-798" data-mp="zm0ia78qd0b7" />
                  {service.title}
                </p>
                <h2 id={`${service.slug}-title`} className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-aubergine sm:text-4xl" data-id="element-799" data-mp="14j0pwml8ab4f">
                  {service.headline}
                </h2>
                <p className="mt-6 max-w-prose text-lg leading-relaxed text-plum/75" data-id="element-800" data-mp="us60588rwte8">{service.description}</p>

                <div className="mt-12 grid gap-10 sm:grid-cols-2" data-id="element-801" data-mp="1ihqne3bymctb">
                  <div data-id="element-802" data-mp="1nb1um5koovlr">
                    <h3 className="font-display text-base font-semibold text-aubergine" data-id="element-803" data-mp="1ncd1wfdc8lhm">Typical organizational needs</h3>
                    <ul className="mt-4 space-y-3" data-id="element-804" data-mp="331m0bjby3hrt">
                      {service.needs.map(n => <li key={n} className="flex gap-3 text-sm leading-relaxed text-plum/75" data-id="element-805" data-mp="3hq8s4kire1zx">
                          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-copper" aria-hidden="true" data-id="element-806" data-mp="3crnqcgnuza9d" />
                          {n}
                        </li>)}
                    </ul>
                  </div>
                  <div data-id="element-807" data-mp="2k3rsh8mjzcb0">
                    <h3 className="font-display text-base font-semibold text-aubergine" data-id="element-808" data-mp="2k4ks7pn3h1jn">Engagement methodology</h3>
                    <ol className="mt-4 space-y-3" data-id="element-809" data-mp="2y06evq5uv98w">
                      {service.methodology.map((m, i) => <li key={m} className="flex gap-3 text-sm leading-relaxed text-plum/75" data-id="element-810" data-mp="dca7uvri4dps" data-mpi={i}>
                          <span className="mt-px w-5 shrink-0 font-display text-xs font-semibold text-copper-deep" data-id="element-811" data-mp="9ap01ottqpmg">{i + 1}.</span>
                          {m}
                        </li>)}
                    </ol>
                  </div>
                </div>
              </div>

              <aside className="lg:col-span-5" data-id="element-812" data-mp="1dljqq9263tud">
                <div className="on-dark relative overflow-hidden rounded-2xl bg-aubergine p-6" data-id="element-813" data-mp="1dks5ns675eic">
                  <ArchitecturalForm variant={variants[index % 3]} tone="dark" className="mx-auto max-w-[240px]" data-id="element-814" data-mp="1ilfuq8av7l9x" />
                  <span className="absolute left-6 top-6 flex h-11 w-11 items-center justify-center rounded-lg border border-copper-light/40 text-copper-light" data-id="element-815" data-mp="1nj7u1ndua9o5">
                    <Icon className="h-5 w-5" aria-hidden="true" data-id="element-816" data-mp="1wbrmg63jfi0j" />
                  </span>
                </div>
                <h3 className="mt-8 font-display text-base font-semibold text-aubergine" data-id="element-817" data-mp="21a2y8mde82le">Example roles</h3>
                <ul className="mt-4 flex flex-wrap gap-2" data-id="element-818" data-mp="26bihq9zxa11o">
                  {service.exampleRoles.map(r => <li key={r} className={`rounded-full border border-plum/15 px-3.5 py-1.5 text-sm text-plum/80 ${alt ? 'bg-porcelain/60' : 'bg-white'}`} data-id="element-819" data-mp="2b9tvgvtc8vp6">
                      {r}
                    </li>)}
                </ul>
                <ButtonLink to={`/employers?service=${service.slug}#inquiry`} arrow className="mt-8" ariaLabel={`Discuss this service: ${service.title}`} data-id="element-820" data-mp="3lsx1o2c0gt4a">
                  Discuss This Service
                </ButtonLink>
              </aside>
            </div>
          </section>;
    })}

      <CtaBand title="Not sure which service fits?" text="Many searches combine elements of several services. Share your situation and we will suggest a sensible starting point." data-id="element-821" data-mp="ddn2p33f440q">
        <ButtonLink to="/employers#inquiry" size="lg" data-id="element-822" data-mp="3o3gbnghxs14">
          Start a Search Conversation
        </ButtonLink>
        <ButtonLink to="/consultants" variant="outline-light" size="lg" data-id="element-823" data-mp="1137ixe86sn6b">
          Contact an Executive Search Consultant
        </ButtonLink>
      </CtaBand>
    </>;
}